import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, imgUrl } from "@/lib/api";
import ChaiPour from "@/components/ChaiPour";
import DragStrip from "@/components/DragStrip";

const CHAPTERS = [
  { n: "01", title: "A qissa, not an agency", body: "Qissa means story. We started as one — a couple of late nights, a shared doc and a stubborn belief that most brands are told badly. We're not a big agency and we don't pretend to be." },
  { n: "02", title: "A tight-knit crew", body: "We're a small studio of designers and strategists working across India and, increasingly, the rest of the world. We stay small on purpose — the people who pitch you are the people who do the work." },
  { n: "03", title: "We're selective, warmly", body: "We're picky about who we work with, and we think you should be too. If we're not the right fit, we'll say so. When it's a yes, you get our full attention and our real opinions." },
  { n: "04", title: "Research first, always", body: "Before we open a single design file, we dig. We track culture, aesthetics, competitors and what your audience is genuinely doing — not what a trend deck says they are." },
];

export default function WhoWeAre() {
  const [c, setC] = useState({});
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get("/content").then((r) => setC(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const strip = projects.filter((p) => p.featured).concat(projects).slice(0, 8).map((p) => p.cover).filter(Boolean);

  return (
    <div className="bg-bg text-black pt-[22vh] md:pt-[24vh]">
      {/* heading */}
      <section className="px-4 md:px-6">
        <div className="font-mono uppercase text-[12px] tracking-[0.12em] text-muted2 mb-5">[ Who we are ]</div>
        <h1 className="font-display uppercase font-[900] tracking-[-0.02em] leading-[0.88] text-6xl sm:text-7xl lg:text-8xl">
          A small studio,<br />big opinions.
        </h1>
      </section>

      {/* chai line + animation */}
      <section className="px-4 md:px-6 pt-20 md:pt-28">
        <div className="flex flex-col md:flex-row md:items-center gap-8 md:gap-14">
          <p className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-[1.3] text-black max-w-[720px]">
            Boutique by choice. Global by accident. <span className="whitespace-nowrap">Chai-powered</span> by necessity.
          </p>
          <ChaiPour className="w-28 md:w-36 h-auto shrink-0" />
        </div>
      </section>

      {/* chapters */}
      <section className="px-4 md:px-6 pt-24 md:pt-36 border-t border-[#E5E5E5] mt-20 md:mt-28">
        {CHAPTERS.map((ch) => (
          <motion.div key={ch.n}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-1 md:grid-cols-12 gap-6 py-12 md:py-16 border-b border-[#E5E5E5]">
            <div className="md:col-span-5">
              <span className="font-mono text-[12px] tracking-[0.12em] text-muted2">[ {ch.n} ]</span>
              <h2 className="font-display uppercase font-[800] tracking-[-0.02em] leading-[0.95] text-3xl sm:text-4xl mt-3">{ch.title}</h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-center">
              <p className="font-serif text-lg md:text-xl leading-[1.7] text-black">{ch.body}</p>
            </div>
          </motion.div>
        ))}
      </section>

      {/* draggable strip */}
      {strip.length > 0 && (
        <section className="pt-20 md:pt-28 pl-4 md:pl-6">
          <div className="font-mono uppercase text-[12px] tracking-[0.12em] text-muted2 mb-6">[ Inside the studio ]</div>
          <DragStrip images={strip} />
        </section>
      )}

      {/* founder */}
      <section className="px-4 md:px-6 pt-28 md:pt-40">
        <div className="font-mono uppercase text-[12px] tracking-[0.12em] text-muted2 mb-8">[ The founder ]</div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            {c.founder_image && (
              <div className="aspect-[4/5] overflow-hidden bg-surface">
                <img src={imgUrl(c.founder_image, 800)} alt={c.founder_name || "Founder"} loading="lazy" className="w-full h-full object-cover grayscale" />
              </div>
            )}
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <h3 className="font-display uppercase font-[900] tracking-[-0.02em] leading-[0.9] text-5xl sm:text-6xl">{c.founder_name || "Tanya Goel"}</h3>
            <p className="font-mono uppercase text-[13px] tracking-[0.1em] text-muted2 mt-4">{c.founder_role || "Founder & Creative Director"}</p>
            <p className="font-serif text-lg leading-[1.7] text-black mt-8 max-w-[560px]">
              {c.about_founder_bio || c.founder_body || "Tanya founded Qissa to build brands with real character — stories told through design, motion and strategy. She leads every project hands-on, from the first sketch to the final screen."}
            </p>
            <a href="https://instagram.com/qissastudio.co" target="_blank" rel="noreferrer" data-testid="founder-instagram"
              className="inline-block mt-8 font-mono uppercase text-[12px] tracking-[0.12em] hover:underline underline-offset-4">[ Follow on Instagram ]</a>
          </div>
        </div>
      </section>

      {/* closing cta */}
      <section className="px-4 md:px-6 pt-28 md:pt-40 pb-10">
        <h2 className="font-display uppercase font-[800] tracking-[-0.02em] leading-[1] text-3xl sm:text-4xl lg:text-5xl max-w-[80%]">
          Enough about us. Tell us your story.
        </h2>
        <Link to="/connect" data-testid="about-cta" className="inline-block mt-8 font-mono uppercase text-[12px] tracking-[0.12em] hover:underline underline-offset-4">[ Book a call ]</Link>
      </section>
    </div>
  );
}
